// Risk appetite definition page (FRMW-003).
// Two-panel layout: taxonomy tree on left, clickable grid editor on right.
// Follows the same structural pattern as admin/roles permission tabs.

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  taxonomyApi,
  appetiteApi,
  likelihoodApi,
  scoringGridApi,
  type TaxonomyNode,
  type AppetiteDefinition,
  type AppetiteCell,
} from '@/lib/api/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CircleDot,
  AlertTriangle,
  Loader2,
  Trash2,
  Pencil,
} from 'lucide-react'
import {
  VIEWBOX_W,
  VIEWBOX_H,
  MARGIN,
  cellWidth,
  cellHeight,
  cellOrigin,
  appetiteKey,
} from '@/components/features/risk-grid/risk-grid-utils'

// --- Tree helpers ---

interface TreeNode extends TaxonomyNode {
  children: TreeNode[]
}

function buildTree(nodes: TaxonomyNode[]): TreeNode[] {
  const sorted = [...nodes].sort((a, b) => a.path.localeCompare(b.path))
  const byId = new Map<string, TreeNode>()
  const roots: TreeNode[] = []

  for (const n of sorted) {
    const treeNode: TreeNode = { ...n, children: [] }
    byId.set(n.id, treeNode)
    if (!n.parent_id) {
      roots.push(treeNode)
    } else {
      byId.get(n.parent_id)?.children.push(treeNode)
    }
  }
  return roots
}

// --- Page component ---

export function AppetitePage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()

  const { toast } = useToast()
  const taxApi = taxonomyApi(api)
  const appApi = appetiteApi(api)
  const lkApi = likelihoodApi(api)
  const sgApi = scoringGridApi(api)

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // --- Queries ---

  const taxonomyQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.taxonomy(slug),
    queryFn: () => taxApi.getActive(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  const scoringGridQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.scoringGrid(slug),
    queryFn: () => sgApi.get(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  const likelihoodQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.likelihood(slug),
    queryFn: () => lkApi.getActive(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  const appetitesQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.appetite(slug),
    queryFn: () => appApi.list(),
  })

  // --- Derived data ---

  const treeNodes = useMemo(
    () => (taxonomyQuery.data ? buildTree(taxonomyQuery.data.nodes) : []),
    [taxonomyQuery.data],
  )

  const allNodes = taxonomyQuery.data?.nodes ?? []

  // Build appetite lookup: nodeId → AppetiteDefinition
  const appetiteByNode = useMemo(() => {
    const map = new Map<string, AppetiteDefinition>()
    for (const d of appetitesQuery.data ?? []) {
      map.set(d.taxonomy_node_id, d)
    }
    return map
  }, [appetitesQuery.data])

  // For a given node, find the nearest ancestor with a definition (for "inherited" display)
  const resolveAppetite = useCallback(
    (nodeId: string): { definition: AppetiteDefinition | null; isOwn: boolean } => {
      const own = appetiteByNode.get(nodeId)
      if (own) return { definition: own, isOwn: true }

      // Walk up the tree via path
      const node = allNodes.find((n) => n.id === nodeId)
      if (!node) return { definition: null, isOwn: false }

      // Build ancestor paths from this node's path
      const parts = node.path.split('.')
      for (let i = parts.length - 1; i >= 0; i--) {
        const ancestorPath = parts.slice(0, i).join('.')
        if (!ancestorPath) continue
        const ancestor = allNodes.find((n) => n.path === ancestorPath)
        if (ancestor) {
          const def = appetiteByNode.get(ancestor.id)
          if (def) return { definition: def, isOwn: false }
        }
      }
      return { definition: null, isOwn: false }
    },
    [appetiteByNode, allNodes],
  )

  const selectedNode = allNodes.find((n) => n.id === selectedNodeId) ?? null
  const selectedAppetite = selectedNodeId ? resolveAppetite(selectedNodeId) : null

  // Build grid config from likelihood levels and scoring grid
  const gridConfig = useMemo(() => {
    if (!scoringGridQuery.data || !likelihoodQuery.data) return null
    const lkLevels = likelihoodQuery.data.levels
    const impactCount = scoringGridQuery.data.target_impact_level_count
    return {
      likelihoodLevels: lkLevels.map((l) => ({ value: l.level_number, label: l.label })),
      impactLevels: Array.from({ length: impactCount }, (_, i) => ({
        value: i + 1,
        label: String(i + 1),
      })),
    }
  }, [scoringGridQuery.data, likelihoodQuery.data])

  // --- Loading / prerequisites ---

  const isLoading =
    taxonomyQuery.isLoading ||
    scoringGridQuery.isLoading ||
    likelihoodQuery.isLoading ||
    appetitesQuery.isLoading

  if (isLoading) return <TableSkeleton />

  if (taxonomyQuery.error && !(taxonomyQuery.error instanceof ApiRequestError && taxonomyQuery.error.status === 404)) {
    return <ErrorState message={t('common:errors.loadFailed', { resource: t('appetite.title') })} technicalDetail={taxonomyQuery.error.message} onRetry={() => taxonomyQuery.refetch()} />
  }

  // Prerequisites checks
  if (!taxonomyQuery.data || allNodes.length === 0) {
    return <PrereqWarning title={t('appetite.title')} description={t('appetite.description')} message={t('appetite.noTaxonomy')} />
  }
  if (!scoringGridQuery.data) {
    return <PrereqWarning title={t('appetite.title')} description={t('appetite.description')} message={t('appetite.noScoringGrid')} />
  }
  if (!likelihoodQuery.data || likelihoodQuery.data.levels.length === 0) {
    return <PrereqWarning title={t('appetite.title')} description={t('appetite.description')} message={t('appetite.noLikelihood')} />
  }

  function toggleExpand(nodeId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  const invalidateAppetites = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.appetite(slug) })
  }

  const handleClearAppetite = (nodeId: string) => {
    appApi.remove(nodeId).then(() => {
      invalidateAppetites()
      toast({ title: t('appetite.deleted') })
    }).catch((err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{t('appetite.title')}</h1>
        <p className="text-muted-foreground">{t('appetite.description')}</p>
      </div>

      <div className="flex h-[calc(100vh-14rem)] gap-0 overflow-hidden rounded-md border">
        {/* Left: Taxonomy tree */}
        <div className="w-2/5 overflow-y-auto border-r p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('appetite.taxonomyNodes')}
          </div>
          {treeNodes.map((node) => (
            <AppetiteTreeRow
              key={node.id}
              node={node}
              depth={0}
              expanded={expanded}
              selectedId={selectedNodeId}
              appetiteByNode={appetiteByNode}
              onToggleExpand={toggleExpand}
              onSelect={setSelectedNodeId}
              onClearAppetite={handleClearAppetite}
            />
          ))}
        </div>

        {/* Right: Grid editor */}
        <div className="w-3/5 overflow-y-auto p-4">
          {selectedNode && gridConfig && selectedAppetite ? (
            <AppetiteGridEditor
              node={selectedNode}
              gridConfig={gridConfig}
              resolved={selectedAppetite}
              onSaved={invalidateAppetites}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t('appetite.selectNode')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Prerequisite warning ---

function PrereqWarning({ title, description, message }: { title: string; description: string; message: string }) {
  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-muted-foreground">{description}</p>
      </div>
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-8 text-muted-foreground">
          <AlertTriangle className="h-5 w-5" />
          <span>{message}</span>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Taxonomy tree row ---

interface AppetiteTreeRowProps {
  node: TreeNode
  depth: number
  expanded: Set<string>
  selectedId: string | null
  appetiteByNode: Map<string, AppetiteDefinition>
  onToggleExpand: (id: string) => void
  onSelect: (id: string) => void
  onClearAppetite: (nodeId: string) => void
}

function AppetiteTreeRow({
  node,
  depth,
  expanded,
  selectedId,
  appetiteByNode,
  onToggleExpand,
  onSelect,
  onClearAppetite,
}: AppetiteTreeRowProps) {
  const { t } = useTranslation(['frameworkConfig'])
  const isExpanded = expanded.has(node.id)
  const hasChildren = node.children.length > 0
  const isSelected = node.id === selectedId
  const hasOwnDef = appetiteByNode.has(node.id)
  const indent = depth * 20

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={cn(
              'flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-muted/50',
              isSelected && 'bg-muted',
            )}
            style={{ paddingLeft: `${indent + 8}px` }}
            onClick={() => {
              onSelect(node.id)
              if (hasChildren && !isExpanded) onToggleExpand(node.id)
            }}
          >
            {/* Expand/collapse chevron */}
            <span
              className="mr-1 w-4 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                if (hasChildren) onToggleExpand(node.id)
              }}
            >
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )
              ) : null}
            </span>

            {/* Appetite indicator */}
            <span className="mr-1.5 flex-shrink-0">
              {hasOwnDef ? (
                <CircleDot className="h-3.5 w-3.5 text-primary" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />
              )}
            </span>

            {/* Node name */}
            <span className={cn('truncate', hasOwnDef && 'font-semibold')}>{node.name}</span>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          <ContextMenuItem onClick={() => onSelect(node.id)}>
            <Pencil className="mr-2 h-4 w-4" />
            {hasOwnDef ? t('appetite.editAppetite') : t('appetite.setAppetite')}
          </ContextMenuItem>
          {hasOwnDef && node.depth > 0 && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onClearAppetite(node.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('appetite.clearAppetite')}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Children */}
      {isExpanded &&
        node.children.map((child) => (
          <AppetiteTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            selectedId={selectedId}
            appetiteByNode={appetiteByNode}
            onToggleExpand={onToggleExpand}
            onSelect={onSelect}
            onClearAppetite={onClearAppetite}
          />
        ))}
    </>
  )
}

// --- Grid editor panel ---

interface GridConfig {
  likelihoodLevels: { value: number; label: string }[]
  impactLevels: { value: number; label: string }[]
}

interface AppetiteGridEditorProps {
  node: TaxonomyNode
  gridConfig: GridConfig
  resolved: { definition: AppetiteDefinition | null; isOwn: boolean }
  onSaved: () => void
}

function AppetiteGridEditor({ node, gridConfig, resolved, onSaved }: AppetiteGridEditorProps) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const appApi = appetiteApi(api)

  // Local cell state — initialise from resolved definition
  const initialCells = useMemo(() => {
    const set = new Set<string>()
    if (resolved.definition) {
      for (const c of resolved.definition.permitted_cells) {
        set.add(appetiteKey(c.likelihood_level, c.impact_level))
      }
    }
    return set
  }, [resolved.definition])

  const initialStatement = resolved.definition?.statement ?? ''

  const [cells, setCells] = useState<Set<string>>(initialCells)
  const [statement, setStatement] = useState(initialStatement)
  const [hasChanges, setHasChanges] = useState(false)

  // Reset local state when node selection or server data changes.
  // Keyed on a stable string so Set identity changes don't cause loops.
  const initialCellsKey = [...initialCells].sort().join(',')
  useEffect(() => {
    setCells(new Set(initialCells))
    setStatement(resolved.definition?.statement ?? '')
    setHasChanges(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCellsKey, node.id])

  function toggleCell(likelihood: number, impact: number) {
    const key = appetiteKey(likelihood, impact)
    setCells((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    setHasChanges(true)
  }

  function selectAll() {
    const all = new Set<string>()
    for (const lk of gridConfig.likelihoodLevels) {
      for (const imp of gridConfig.impactLevels) {
        all.add(appetiteKey(lk.value, imp.value))
      }
    }
    setCells(all)
    setHasChanges(true)
  }

  function clearAll() {
    setCells(new Set())
    setHasChanges(true)
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const permitted_cells: AppetiteCell[] = []
      for (const key of cells) {
        const parts = key.split('-').map(Number)
        permitted_cells.push({ likelihood_level: parts[0]!, impact_level: parts[1]! })
      }
      return appApi.set(node.id, {
        permitted_cells,
        statement: statement.trim() || null,
      })
    },
    onSuccess: () => {
      onSaved()
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.appetite(slug) })
      setHasChanges(false)
      toast({ title: t('appetite.updated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => appApi.remove(node.id),
    onSuccess: () => {
      onSaved()
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.appetite(slug) })
      setHasChanges(false)
      toast({ title: t('appetite.deleted') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  const cols = gridConfig.likelihoodLevels.length
  const rows = gridConfig.impactLevels.length
  const cw = cellWidth(cols)
  const ch = cellHeight(rows)

  // Status line
  const statusText = resolved.isOwn
    ? t('appetite.ownDefinition')
    : resolved.definition
      ? t('appetite.inherited', { name: resolved.definition.taxonomy_node_name })
      : t('appetite.noDefinition')

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">{node.name}</h2>
        <p className="text-sm text-muted-foreground">{statusText}</p>
      </div>

      {/* Interactive grid */}
      <div className="max-w-md">
        <svg
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          width="100%"
          role="img"
          aria-label={`Risk appetite grid for ${node.name}`}
          className="block"
        >
          {/* Cell rectangles — clickable */}
          {gridConfig.likelihoodLevels.map((lLevel) =>
            gridConfig.impactLevels.map((iLevel) => {
              const origin = cellOrigin(lLevel.value, iLevel.value, cols, rows)
              const key = appetiteKey(lLevel.value, iLevel.value)
              const inAppetite = cells.has(key)
              return (
                <rect
                  key={`cell-${lLevel.value}-${iLevel.value}`}
                  x={origin.x}
                  y={origin.y}
                  width={cw}
                  height={ch}
                  fill={inAppetite ? 'hsl(var(--grid-appetite-in))' : 'hsl(var(--grid-appetite-out))'}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                  className="cursor-pointer transition-colors hover:opacity-80"
                  onClick={() => toggleCell(lLevel.value, iLevel.value)}
                />
              )
            }),
          )}

          {/* Y-axis labels (impact) */}
          {gridConfig.impactLevels.map((level) => {
            const origin = cellOrigin(1, level.value, cols, rows)
            return (
              <text
                key={`y-${level.value}`}
                x={MARGIN.left - 6}
                y={origin.y + ch / 2}
                textAnchor="end"
                dominantBaseline="central"
                fontSize={11}
                fill="hsl(var(--muted-foreground))"
              >
                {level.value}
              </text>
            )
          })}

          {/* Y-axis title */}
          <text
            x={12}
            y={MARGIN.top + (VIEWBOX_H - MARGIN.top - MARGIN.bottom) / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fill="hsl(var(--muted-foreground))"
            transform={`rotate(-90, 12, ${MARGIN.top + (VIEWBOX_H - MARGIN.top - MARGIN.bottom) / 2})`}
          >
            Impact →
          </text>

          {/* X-axis labels (likelihood) */}
          {gridConfig.likelihoodLevels.map((level) => {
            const origin = cellOrigin(level.value, 1, cols, rows)
            return (
              <text
                key={`x-${level.value}`}
                x={origin.x + cw / 2}
                y={MARGIN.top + rows * ch + 18}
                textAnchor="middle"
                fontSize={11}
                fill="hsl(var(--muted-foreground))"
              >
                {level.value}
              </text>
            )
          })}

          {/* X-axis title */}
          <text
            x={MARGIN.left + (VIEWBOX_W - MARGIN.left - MARGIN.right) / 2}
            y={VIEWBOX_H - 8}
            textAnchor="middle"
            fontSize={11}
            fill="hsl(var(--muted-foreground))"
          >
            Likelihood →
          </text>
        </svg>
      </div>

      {/* Appetite statement */}
      <div>
        <label
          htmlFor="appetite-statement"
          className="mb-1 block text-sm font-medium"
        >
          {t('appetite.statementLabel')}
        </label>
        <textarea
          id="appetite-statement"
          value={statement}
          onChange={(e) => {
            setStatement(e.target.value)
            setHasChanges(true)
          }}
          placeholder={t('appetite.statementPlaceholder')}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      {/* Cell count + bulk actions */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {t('appetite.cellsSelected', { count: cells.size })}
        </span>
        <Button variant="ghost" size="sm" onClick={selectAll}>
          {t('appetite.allCells')}
        </Button>
        <Button variant="ghost" size="sm" onClick={clearAll}>
          {t('appetite.noCells')}
        </Button>
      </div>

      {/* Save / Remove actions */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || cells.size === 0}
        >
          {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {saveMutation.isPending ? t('appetite.saving') : t('appetite.save')}
        </Button>

        {resolved.isOwn && node.depth > 0 && (
          <Button
            variant="outline"
            onClick={() => removeMutation.mutate()}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            {removeMutation.isPending ? t('appetite.removing') : t('appetite.remove')}
          </Button>
        )}

        {hasChanges && (
          <span className="text-xs text-muted-foreground">Unsaved changes</span>
        )}
      </div>
    </div>
  )
}
